import { useMemo } from "react";

import { Specimen } from "pwapi";

import {
  fetchSlideClassification,
  fetchSpecimenClassification,
} from "shared/actions/aiActions";
import { useHasLoaded, useIsLoading } from "shared/hooks/requestStatusHooks";
import { LesionLine } from "shared/reducers/aiReducer";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";

import { slideClassification } from "components/viewer/dialogs/utils";
import { selectSpecimenClassification } from "selectors/classificationSelector";
import { useAppSelector } from "store/hooks";

export const useHasAILoaded = (slideDetailObject: SlideDetailState) => {
  const latestIndividualClassification = slideClassification(slideDetailObject);
  const specimenClassificationLoaded = useHasLoaded(
    fetchSpecimenClassification,
    {
      parentLookupCaseId: slideDetailObject.case_id || -1,
      id: slideDetailObject.specimen?.id || -1,
    }
  );
  const slideClassificationLoaded = useHasLoaded(fetchSlideClassification, {
    id: latestIndividualClassification?.id || -1,
    slide_id: slideDetailObject.id || -1,
  });

  return slideDetailObject?.specimen?.id
    ? specimenClassificationLoaded
    : slideClassificationLoaded;
};

export const useIsAILoading = (slideDetailObject: SlideDetailState) => {
  const latestIndividualClassification = slideClassification(slideDetailObject);
  const specimenClassificationLoading = useIsLoading(
    fetchSpecimenClassification,
    {
      parentLookupCaseId: slideDetailObject.case_id || -1,
      id: slideDetailObject.specimen?.id || -1,
    }
  );
  const slideClassificationLoading = useIsLoading(fetchSlideClassification, {
    id: latestIndividualClassification?.id || -1,
    slide_id: slideDetailObject.id || -1,
  });

  return slideDetailObject?.specimen?.id
    ? specimenClassificationLoading
    : slideClassificationLoading;
};

export const usePositiveLesionLines = (
  positiveLesionLines?: LesionLine[],
  currentSpecimen?: Specimen
) => {
  const specimenClassification = useAppSelector((state) =>
    selectSpecimenClassification(state, currentSpecimen?.id)
  );

  return useMemo(() => {
    return [...new Set(positiveLesionLines?.map((ll) => ll.diagnosis_name))]
      .map((diagnosis_name) => {
        const classificationesWithLines =
          specimenClassification?.filter((s) =>
            s?.lesion_lines?.some(
              (ll) =>
                ll.diagnosis_name === diagnosis_name &&
                ll.diagnosis_result === "POSITIVE"
            )
          ) || [];

        const linesOfName = classificationesWithLines
          .flatMap((c) => c?.lesion_lines || [])
          .map((c) => ({
            ...c,
            subtypes: c.subtypes
              ? [...c.subtypes].sort(
                  (a, b) =>
                    (a?.severity ?? -Infinity) - (b?.severity ?? -Infinity)
                )
              : c.subtypes,
          }))
          .filter(
            (ll) =>
              ll.diagnosis_name === diagnosis_name &&
              ll.diagnosis_result === "POSITIVE"
          )
          .filter((v) => v)
          .sort(
            (a, b) =>
              Math.max(
                ...(b.subtypes?.map((s) => s.severity ?? -Infinity) ?? [
                  -Infinity,
                ])
              ) -
              Math.max(
                ...(a.subtypes?.map((s) => s.severity ?? -Infinity) ?? [
                  -Infinity,
                ])
              )
          );

        return {
          diagnosis_name,
          linesOfName,
          subtypes: [
            ...new Set(
              positiveLesionLines
                ?.filter((ll) => ll.diagnosis_name === diagnosis_name)
                .flatMap((ll) =>
                  ll.subtypes?.filter((s) =>
                    ["POSITIVE", "INCONCLUSIVE"].includes(s.diagnosis_result)
                  )
                )
                .sort(
                  (a, b) =>
                    (a?.severity ?? -Infinity) - (b?.severity ?? -Infinity)
                )
                .map(
                  (s) =>
                    `${s?.diagnosis}${
                      s?.diagnosis_result === "INCONCLUSIVE"
                        ? " (Inconclusive)"
                        : ""
                    }`
                )
            ),
          ],
          slide_ids: classificationesWithLines.map((s) => s?.slide_id),
        };
      })
      .sort(
        (a, b) =>
          Math.max(
            ...b.linesOfName.flatMap(
              (l) =>
                l.subtypes?.map((s) => s.severity ?? -Infinity) ?? [-Infinity]
            )
          ) -
          Math.max(
            ...a.linesOfName.flatMap(
              (l) =>
                l.subtypes?.map((s) => s.severity ?? -Infinity) ?? [-Infinity]
            )
          )
      );
  }, [positiveLesionLines, specimenClassification]);
};

export type PositiveLesionLine = ReturnType<typeof usePositiveLesionLines>[0];
